/**
 * Phase 7.3.2 — Sound design overhaul.
 *
 * All sounds are pure WebAudio synthesis — no external assets, no licence
 * questions. Each sound has its own per-type volume scalar, multiplied
 * by the user's master `audioVolume` (0–1).
 *
 * Available sounds:
 *   - playAlertBeep   : new mission spawn (two-tone pager "bing-bong")
 *   - playRadioBleep  : on-scene radio request (white-noise click + 800Hz beep)
 *   - playSuccess     : mission cleared (rising 2-tone)
 *   - playFailure     : mission failed (descending 3-tone)
 *   - playEscalation  : mission escalated (low sawtooth alert)
 *   - startSirenLoop  : ambient siren (two-tone, looped, very low volume)
 *   - stopSirenLoop   : fade out
 *   - playTestBeep    : settings audio test
 */

type AudioContextCtor = typeof AudioContext;

let ctx: AudioContext | null = null;

function getContext(): AudioContext | null {
  if (ctx) return ctx;
  if (typeof window === 'undefined') return null;
  const Ctor: AudioContextCtor | undefined =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext;
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
  } catch {
    return null;
  }
  return ctx;
}

function ensureRunning(audio: AudioContext): void {
  if (audio.state === 'suspended') {
    void audio.resume();
  }
}

interface ToneOptions {
  frequency: number;
  startAt: number;
  durationSec: number;
  volume: number;
  type?: OscillatorType;
}

function emitTone(audio: AudioContext, opts: ToneOptions): void {
  try {
    const osc = audio.createOscillator();
    const gain = audio.createGain();
    osc.type = opts.type ?? 'sine';
    osc.frequency.value = opts.frequency;
    gain.gain.setValueAtTime(0.0001, opts.startAt);
    gain.gain.exponentialRampToValueAtTime(opts.volume, opts.startAt + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, opts.startAt + opts.durationSec);
    osc.connect(gain).connect(audio.destination);
    osc.start(opts.startAt);
    osc.stop(opts.startAt + opts.durationSec + 0.02);
  } catch {
    // ignore
  }
}

function emitWhiteNoiseBurst(
  audio: AudioContext,
  startAt: number,
  durationSec: number,
  volume: number,
): void {
  try {
    const sampleCount = Math.max(1, Math.floor(audio.sampleRate * durationSec));
    const buf = audio.createBuffer(1, sampleCount, audio.sampleRate);
    const channel = buf.getChannelData(0);
    for (let i = 0; i < sampleCount; i++) channel[i] = Math.random() * 2 - 1;
    const src = audio.createBufferSource();
    src.buffer = buf;
    const gain = audio.createGain();
    gain.gain.setValueAtTime(volume, startAt);
    gain.gain.exponentialRampToValueAtTime(0.0001, startAt + durationSec);
    src.connect(gain).connect(audio.destination);
    src.start(startAt);
    src.stop(startAt + durationSec + 0.02);
  } catch {
    // ignore
  }
}

const RESOLVE_VOLUME = (master: number, scale: number): number =>
  Math.max(0, Math.min(1, master)) * scale;

/* ─────────────────────────── Public API ─────────────────────────── */

interface PlayOpts {
  muted?: boolean;
  masterVolume?: number;
}

export function playAlertBeep({ muted, masterVolume = 1 }: PlayOpts = {}): void {
  if (muted) return;
  const audio = getContext();
  if (!audio) return;
  ensureRunning(audio);
  const v = RESOLVE_VOLUME(masterVolume, 0.18);
  const t = audio.currentTime;
  // Pager-style "bing-bong": 440 Hz (100ms) → pause 50ms → 660 Hz (100ms)
  emitTone(audio, { frequency: 440, startAt: t, durationSec: 0.1, volume: v });
  emitTone(audio, { frequency: 660, startAt: t + 0.15, durationSec: 0.12, volume: v });
}

export function playRadioBleep({ muted, masterVolume = 1 }: PlayOpts = {}): void {
  if (muted) return;
  const audio = getContext();
  if (!audio) return;
  ensureRunning(audio);
  const v = RESOLVE_VOLUME(masterVolume, 0.16);
  const t = audio.currentTime;
  emitWhiteNoiseBurst(audio, t, 0.05, v * 0.8);
  emitTone(audio, { frequency: 800, startAt: t + 0.06, durationSec: 0.1, volume: v });
}

export function playSuccess({ muted, masterVolume = 1 }: PlayOpts = {}): void {
  if (muted) return;
  const audio = getContext();
  if (!audio) return;
  ensureRunning(audio);
  const v = RESOLVE_VOLUME(masterVolume, 0.18);
  const t = audio.currentTime;
  emitTone(audio, { frequency: 440, startAt: t, durationSec: 0.1, volume: v });
  emitTone(audio, { frequency: 660, startAt: t + 0.1, durationSec: 0.12, volume: v });
}

export function playFailure({ muted, masterVolume = 1 }: PlayOpts = {}): void {
  if (muted) return;
  const audio = getContext();
  if (!audio) return;
  ensureRunning(audio);
  const v = RESOLVE_VOLUME(masterVolume, 0.18);
  const t = audio.currentTime;
  emitTone(audio, { frequency: 660, startAt: t, durationSec: 0.1, volume: v });
  emitTone(audio, { frequency: 440, startAt: t + 0.1, durationSec: 0.1, volume: v });
  emitTone(audio, { frequency: 330, startAt: t + 0.2, durationSec: 0.12, volume: v });
}

export function playEscalation({ muted, masterVolume = 1 }: PlayOpts = {}): void {
  if (muted) return;
  const audio = getContext();
  if (!audio) return;
  ensureRunning(audio);
  const v = RESOLVE_VOLUME(masterVolume, 0.22);
  const t = audio.currentTime;
  emitTone(audio, {
    frequency: 220,
    startAt: t,
    durationSec: 0.4,
    volume: v,
    type: 'sawtooth',
  });
}

export function playTestBeep({ masterVolume = 1 }: PlayOpts = {}): void {
  const audio = getContext();
  if (!audio) return;
  ensureRunning(audio);
  const v = RESOLVE_VOLUME(masterVolume, 0.2);
  const t = audio.currentTime;
  emitTone(audio, { frequency: 660, startAt: t, durationSec: 0.18, volume: v });
}

/**
 * Phase 8.1.4 — Siren burst (1.5 s) instead of continuous loop.
 * Plays a two-tone alternating siren that fades in and out, used at the
 * Status 2 → 3 transition (vehicle abrückt).
 *
 * The legacy startSirenLoop / stopSirenLoop / setSirenVolume API is gone
 * (the Phase-7 ambient loop was overpowering). Callers should switch to
 * `playSirenBurst({ muted, masterVolume })`.
 */
export function playSirenBurst({ muted, masterVolume = 1 }: PlayOpts = {}): void {
  if (muted) return;
  const audio = getContext();
  if (!audio) return;
  ensureRunning(audio);

  const v = RESOLVE_VOLUME(masterVolume, 0.12);
  const t0 = audio.currentTime;
  // 4 tone alternations over 1.5 s (375 ms each)
  const tones = [700, 470, 700, 470];
  const each = 0.375;
  for (let i = 0; i < tones.length; i++) {
    emitTone(audio, {
      frequency: tones[i],
      startAt: t0 + i * each,
      durationSec: each * 0.95,
      volume: v,
    });
  }
}

/* ──────────── Phase 12.2.5 — File-backed sound layer ──────────── */

import { SOUND_MAP, type SoundEvent, SOUND_FILES_TO_PRELOAD } from '@/data/assetManifest';

/** Cache of preloaded HTMLAudioElement instances keyed by file path. */
const audioPool = new Map<string, HTMLAudioElement>();

/**
 * Preloads all known sound files in a single pass. Safe to call multiple
 * times — already-loaded files are reused.
 *
 * Sounds are 1-shot (not looped). Cloning an HTMLAudioElement before each
 * play allows overlapping triggers (e.g. multiple sirens within 100 ms).
 */
export function preloadSounds(): void {
  if (typeof window === 'undefined') return;
  for (const path of SOUND_FILES_TO_PRELOAD) {
    if (audioPool.has(path)) continue;
    const el = new Audio(path);
    el.preload = 'auto';
    audioPool.set(path, el);
  }
}

/**
 * Phase 13.6 — Per-event cooldown to prevent rapid-fire duplicate plays.
 * Every event has its own cooldown window so an aggressive tick can't
 * spam a single sound. 500 ms is below human re-trigger perception but
 * above any legitimate event cluster.
 */
const SOUND_COOLDOWN_MS = 500;
const lastPlayedAt = new Map<SoundEvent, number>();

/**
 * Phase 12.2.5 — Play a sound event. Tries the file mapping first; falls
 * back to the synthetic equivalent if no file is configured (or the file
 * fails to play).
 *
 * Phase 13.6 — Cooldown gate: silently drops a play if the same event
 * fired less than {@link SOUND_COOLDOWN_MS} ago.
 */
export function playSoundEvent(
  event: SoundEvent,
  opts: PlayOpts = {},
): void {
  if (opts.muted) return;
  // Cooldown gate.
  const now = Date.now();
  const last = lastPlayedAt.get(event) ?? 0;
  if (now - last < SOUND_COOLDOWN_MS) return;
  lastPlayedAt.set(event, now);

  const def = SOUND_MAP[event];
  const masterVolume = opts.masterVolume ?? 1;
  const finalVolume = Math.max(0, Math.min(1, masterVolume * def.volume));

  if (def.file) {
    if (typeof window === 'undefined') return;
    let proto = audioPool.get(def.file);
    if (!proto) {
      proto = new Audio(def.file);
      proto.preload = 'auto';
      audioPool.set(def.file, proto);
    }
    try {
      const inst = proto.cloneNode(true) as HTMLAudioElement;
      inst.volume = finalVolume;
      void inst.play().catch(() => {
        // Browser autoplay policy or load error — gracefully fall through.
        playSyntheticFallback(event, opts);
      });
      return;
    } catch {
      playSyntheticFallback(event, opts);
      return;
    }
  }
  playSyntheticFallback(event, opts);
}

/** Pure-synthesis fallback paths (the original Phase-7 sound design). */
function playSyntheticFallback(event: SoundEvent, opts: PlayOpts): void {
  switch (event) {
    case 'mission-spawn':
      playAlertBeep(opts);
      return;
    case 'siren-burst':
      playSirenBurst(opts);
      return;
    case 'arrival':
    case 'radio-bleep':
      playRadioBleep(opts);
      return;
    case 'success':
    case 'level-up':
      playSuccess(opts);
      return;
    case 'failure':
      playFailure(opts);
      return;
    case 'escalation':
      playEscalation(opts);
      return;
    case 'arrest':
    case 'upkeep-tick':
      playTestBeep(opts);
      return;
  }
}
